import { CSSProperties, useMemo } from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router";

import { Minimize2 } from "lucide-react";
import { StatusPill } from "nazare-ui/components";

import { useDocumentTitle } from "../../../../use-document-title";
import { usePageSearch } from "../../../../global-search-context";
import { ScrollShadow, TABLE_HEADER_HEIGHT } from "../../../../scroll-shadow";
import { InfiniteScrollSentinel, SkeletonRow } from "../../../../skeleton-row";
import { useInfiniteRows } from "../../../../use-infinite-rows";
import { useScrollEdges } from "../../../../use-scroll-edges";
import { useSimulatedLoad } from "../../../../use-simulated-load";
import styles from "./active.module.css";

type ActiveRun = {
  /** `wrun_<ULID>` — same id shape as the reference dashboard. */
  id: string;
  workflowName: string;
  elapsed: string;
};

/**
 * Static mock runs, longest-running first — same shape/ordering convention
 * as the dashboard's "Active runs" card (`../summary-cards.tsx`), extended
 * to a full table's worth of rows. There is no live workflow-orchestration
 * backend in `platform` yet, so every run here is permanently "Running".
 */
const ACTIVE_RUNS: ActiveRun[] = [
  { id: "wrun_01KX29S346Q3D25VT4F5V37E3", workflowName: "ingestGmailInbox", elapsed: "47m 12s" },
  { id: "wrun_01KWZS3E28JT97KB6CQ643DZV", workflowName: "ingestGmailInbox", elapsed: "41m 03s" },
  { id: "wrun_01KX2XXQKFBF5KZNWJ47TAN9Z", workflowName: "ingestGmailManager", elapsed: "38m 47s" },
  { id: "wrun_01KX224MNPZX45HY43KWJRP1X", workflowName: "ingestGmailInbox", elapsed: "35m 20s" },
  { id: "wrun_01KX2A7Z3DJ8FSSZ5AWSH8VHT", workflowName: "weeklySentimentSummary", elapsed: "33m 51s" },
  { id: "wrun_01KX2RE95B9EE0ZBGJ09TQM83", workflowName: "ingestGmailInbox", elapsed: "29m 14s" },
  { id: "wrun_01KX2SSSS6YS3C4DWA7N36096", workflowName: "dailyReportAlerts", elapsed: "27m 38s" },
  { id: "wrun_01KX214DR9GPQY77ZXYYK596N", workflowName: "ingestGmailInbox", elapsed: "24m 02s" },
  { id: "wrun_01KWZGYA1DQ91K5GQAPENECFS", workflowName: "ingestGmailInbox", elapsed: "22m 45s" },
  { id: "wrun_01KWZECZP11HYGCPWPQ5E6EYC", workflowName: "sentimentFormChaser", elapsed: "20m 19s" },
  { id: "wrun_01KX2DY0YP57RCYBVN5SXS5AA", workflowName: "ingestGmailInbox", elapsed: "18m 53s" },
  { id: "wrun_01KX319X9YP981068VCD1GDJF", workflowName: "weeklyReportAlerts", elapsed: "17m 06s" },
  { id: "wrun_01KWZMGT83PXT891WB09B9Y73", workflowName: "ingestGmailInbox", elapsed: "15m 44s" },
  { id: "wrun_01KX2Y63FCH26W14WMCHWYFGC", workflowName: "agendaReminder", elapsed: "14m 21s" },
  { id: "wrun_01KX28T7SWM4FV4DK79Q9G8XE", workflowName: "ingestGmailInbox", elapsed: "13m 58s" },
  { id: "wrun_01KWZ6SZAEAVSNTCPM5Q1NXW1", workflowName: "ingestGmailInbox", elapsed: "12m 37s" },
  { id: "wrun_01KX2NJ47E65GH2BH8VGS9ZM5", workflowName: "extractMetrics", elapsed: "11m 15s" },
  { id: "wrun_01KX23BV4H15G5E4G7X0NTH82", workflowName: "ingestGmailInbox", elapsed: "9m 42s" },
  { id: "wrun_01KWZF7AG3BCKKDJWBHP1G201", workflowName: "ingestGmailInbox", elapsed: "8m 44s" },
  { id: "wrun_01KWZCYFW6VZSKDENC8SP3804", workflowName: "identifyCompanyUpdate", elapsed: "7m 21s" },
  { id: "wrun_01KWZGVA35RJFJ2XBAHW0GQNM", workflowName: "extractMetrics", elapsed: "6m 08s" },
  { id: "wrun_01KX32KDPB0NR5YHCF05G59S2", workflowName: "sendNotification", elapsed: "5m 33s" },
  { id: "wrun_01KX21KKE59RMZ9J92V81E512", workflowName: "ingestGmailInbox", elapsed: "4m 47s" },
  { id: "wrun_01KX3Q6RW31FZG0X454YG4GFD", workflowName: "ingestGmailInbox", elapsed: "4m 07s" },
  { id: "wrun_01KX3XZR4YJ2C49NGK80Y3ZH6", workflowName: "ingestGmailInbox", elapsed: "3m 55s" },
  { id: "wrun_01KWZDZJJXXX7CK5Y1JX4WHRD", workflowName: "evalMetricsExtraction", elapsed: "2m 38s" },
  { id: "wrun_01KX3459GQ8H7QEZZS1A0ZWSK", workflowName: "identifyCompanyUpdate", elapsed: "1m 52s" },
  { id: "wrun_01KWZ9TPRM7N0MNS7C0JGQ4SR", workflowName: "extractMetrics", elapsed: "1m 09s" },
  { id: "wrun_01KWZ4QVH3H63J9FHVMCQV1SD", workflowName: "identifyCompanyUpdate", elapsed: "47s" },
  { id: "wrun_01KWZ53TW8JZ38AYTNJKGGSFK", workflowName: "identifyCompanyUpdate", elapsed: "21s" },
];

/**
 * A busy system realistically has more than 30 workflows in flight at once —
 * that was just the hand-authored "recent history" sample above.
 * `TOTAL_ACTIVE_RUN_COUNT` is the real, accurate total this page claims (shown
 * in the header), and `getActiveRun(index)` below extends the hardcoded
 * `ACTIVE_RUNS` array out to that count with deterministic synthetic rows for
 * infinite scroll. Kept small relative to the historical `/workflows/runs`
 * log (1,247 there) since every row here is, by definition, CURRENTLY
 * running — there's a natural ceiling on concurrent runs that a completed-run
 * history doesn't have.
 */
const TOTAL_ACTIVE_RUN_COUNT = 68;

/** Deterministic 32-bit string hash (FNV-1a) + seeded PRNG — same technique
 * as `runs.tsx`'s `generateSyntheticRun`, so the SAME index always produces
 * the SAME synthetic row (no `Math.random()`/`Date.now()`). */
function hashSeed(input: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const ULID_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

function syntheticUlid(rand: () => number): string {
  let id = "";
  for (let i = 0; i < 26; i += 1) {
    id += ULID_ALPHABET[Math.floor(rand() * ULID_ALPHABET.length)];
  }
  return `wrun_${id}`;
}

function formatElapsed(totalSeconds: number): string {
  return totalSeconds >= 60
    ? `${Math.floor(totalSeconds / 60)}m ${String(totalSeconds % 60).padStart(2, "0")}s`
    : `${totalSeconds}s`;
}

/** Distinct workflow names already present in `ACTIVE_RUNS` — the pool
 * synthetic rows are drawn from, same reuse-existing-pool convention as
 * `runs.tsx`'s `WORKFLOW_FILTER_VALUES`/`SECONDARY_POOL`. */
const WORKFLOW_POOL = Array.from(
  new Set(ACTIVE_RUNS.map((run) => run.workflowName)),
);

// The last (shortest-elapsed) hardcoded row's elapsed time, in seconds —
// synthetic rows continue counting DOWN from here, never from a live clock
// (this component never calls `Date.now()`/`new Date()`).
const LAST_HARDCODED_ELAPSED_SECONDS = 21;

/** A run beyond the hand-authored `ACTIVE_RUNS` sample — deterministic per
 * `index`, so scrolling to the same position always shows the same row. The
 * hardcoded array is ordered longest-running first, so each synthetic row
 * gets a SHORTER elapsed than the row before it: a fixed 2s step down, with
 * jitter kept smaller than the step (0 or 1s) so the sequence stays strictly
 * decreasing no matter how the jitter rolls — never a LATER row rendering a
 * LONGER elapsed than an EARLIER row, which would read as a real bug in this
 * duration-sorted table (same fixed-step-plus-small-jitter technique as
 * `runs.tsx`'s `generateSyntheticRun`, applied to a countdown instead of a
 * "Created" timestamp). Once the countdown would go below 1s it clamps
 * there — a busy system plausibly has many runs that only just started.
 */
function generateSyntheticActiveRun(index: number): ActiveRun {
  const rand = mulberry32(hashSeed(`active-${index}`));
  const workflowName = WORKFLOW_POOL[Math.floor(rand() * WORKFLOW_POOL.length)];

  const rowsBeyond = index - ACTIVE_RUNS.length + 1;
  const jitter = Math.floor(rand() * 2); // 0 or 1 — always smaller than the 2s step
  const elapsedSeconds = Math.max(
    1,
    LAST_HARDCODED_ELAPSED_SECONDS - rowsBeyond * 2 - jitter,
  );

  return {
    id: syntheticUlid(rand),
    workflowName,
    elapsed: formatElapsed(elapsedSeconds),
  };
}

function getActiveRun(index: number): ActiveRun {
  return index < ACTIVE_RUNS.length
    ? ACTIVE_RUNS[index]
    : generateSyntheticActiveRun(index);
}

/**
 * Full-page "maximised" view of the dashboard's "Active runs" card (see
 * `../summary-cards.tsx`). Reached via a shared-element grow animation from
 * that card, hence the matching `viewTransitionName` below — the browser's
 * View Transitions API morphs the small card into this full page instead of
 * cross-fading. The shell's breadcrumbs already read "Workflows > Active
 * runs" for this route, so there is no page-level heading here, just the
 * table card itself.
 */
export function WorkflowActiveRunsPage() {
  useDocumentTitle("Active Runs");

  const navigate = useNavigate();
  const goToDashboard = () => {
    if (!document.startViewTransition) {
      navigate("/workflows");
      return;
    }
    document.startViewTransition(() => flushSync(() => navigate("/workflows")));
  };

  // Initial-mount loading state — separate from `isLoadingMore` below, which
  // only covers infinite-scroll pagination once the page has already
  // rendered real rows. Nothing actually fetches (no backend), but a page
  // this data-dense should still show a believable brief loading state
  // instead of snapping straight to final content.
  const isInitialLoading = useSimulatedLoad();

  // Filtering happens through the shell's ONE global search box (top
  // header) instead of a redundant local input — usePageSearch sets that
  // box's placeholder to this page's hint text while mounted, and returns
  // whatever the user has typed into it.
  const filter = usePageSearch("Filter by workflow name");

  const { ref: tableWrapRef, atTop, atBottom } = useScrollEdges<HTMLDivElement>();
  const { loadedCount, isLoadingMore, hasMore, sentinelRef } = useInfiniteRows({
    totalCount: TOTAL_ACTIVE_RUN_COUNT,
    initialCount: ACTIVE_RUNS.length,
    pageSize: 20,
    scrollRootRef: tableWrapRef,
  });

  // Filtering applies to whatever's been loaded so far, same as any
  // client-side filter over an infinite-scrolled list — it doesn't fetch
  // ahead to find matches beyond `loadedCount`.
  const loadedRuns = useMemo(
    () => Array.from({ length: loadedCount }, (_, index) => getActiveRun(index)),
    [loadedCount],
  );

  const filteredRuns = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) return loadedRuns;
    return loadedRuns.filter((run) =>
      run.workflowName.toLowerCase().includes(query),
    );
  }, [loadedRuns, filter]);

  const isFiltered = filter.trim().length > 0;

  return (
    <div className={styles.page}>
      <section
        className={styles.card}
        style={{ viewTransitionName: "wf-card-active" } as unknown as CSSProperties}
      >
        <header className={styles.header}>
          <h2 className={styles.title}>Active runs</h2>
          <span className={styles.count}>
            {isFiltered
              ? `${filteredRuns.length} of ${loadedCount} loaded`
              : TOTAL_ACTIVE_RUN_COUNT}
          </span>
          <button
            type="button"
            className={styles.minimiseButton}
            onClick={goToDashboard}
            aria-label="Minimise to dashboard"
            title="Minimise to dashboard"
          >
            <Minimize2 className={styles.minimiseIcon} aria-hidden="true" />
          </button>
        </header>

        <div className={styles.scrollShadowWrapper}>
        <div ref={tableWrapRef} className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th} scope="col">
                  Status
                </th>
                <th className={styles.th} scope="col">
                  Workflow
                </th>
                <th className={styles.th} scope="col">
                  Run ID
                </th>
                <th className={`${styles.th} ${styles.thRight}`} scope="col">
                  Elapsed
                </th>
              </tr>
            </thead>
            <tbody>
              {isInitialLoading ? (
                Array.from({ length: 8 }, (_, index) => (
                  <SkeletonRow key={`initial-skeleton-${index}`} columns={4} />
                ))
              ) : (
                <>
                  {filteredRuns.map((run) => (
                    <tr key={run.id} className={styles.row}>
                      <td className={styles.td}>
                        <StatusPill tone="info" className={styles.runningPill}>
                          <span className={styles.pulseDot} aria-hidden="true" />
                          Running
                        </StatusPill>
                      </td>
                      <td className={styles.td}>{run.workflowName}</td>
                      <td className={`${styles.td} ${styles.runId}`}>{run.id}</td>
                      <td className={`${styles.td} ${styles.elapsed}`}>{run.elapsed}</td>
                    </tr>
                  ))}
                  {filteredRuns.length === 0 && (
                    <tr>
                      <td className={styles.emptyCell} colSpan={4}>
                        No active runs match "{filter}".
                      </td>
                    </tr>
                  )}
                  {hasMore && (
                    <InfiniteScrollSentinel sentinelRef={sentinelRef} columns={4} />
                  )}
                  {isLoadingMore &&
                    Array.from({ length: 3 }, (_, index) => (
                      <SkeletonRow key={`skeleton-${index}`} columns={4} />
                    ))}
                </>
              )}
            </tbody>
          </table>
        </div>
        <ScrollShadow atTop={atTop} atBottom={atBottom} topOffset={TABLE_HEADER_HEIGHT} />
        </div>
      </section>
    </div>
  );
}
